import { memo, useMemo } from 'react'
import { PlotTypesMenuItemType, SeriesOutput } from '../../chart.typings'
import { Box, FormControlLabel, Grid, MenuItem, Select, Typography } from '@mui/material'
import SettingsNumericSlider from '../../../colorSelector/settingsNumericSlider/settingsNumericSlider'
import SeriesOutputPlotTypesView from './seriesOutputPlotTypes'
import {
    AREA_FILL_BOTTOM_COLOR_TITLE,
    AREA_FILL_TOP_COLOR_TITLE,
    getChartTypeFromOutput,
    getOutputLineColor,
    isAreaFillColorTitle,
} from '../../../../util/chart'
import {
    Indicator,
    IndicatorOutput_OutputStyle,
    IndicatorOutput_OutputType,
    indicatorOutput_OutputTypeFromJSON
} from '../../../../proto/generated/indicator'
import { ChartSubType, ChartType } from '../../../../proto/generated/chart'
import Checkbox from '@mui/material/Checkbox'
import ColorSelector, { LineThickness } from '../../../colorSelector/colorSelector'
import { ChartLineStyle, clampLineThickness } from '../../../colorSelector/lineStyle.types'
import tinycolor from 'tinycolor2'
import {
    clampOiProfileBarThickness,
    clampOiProfileBarWidthPercent,
    OI_PROFILE_DEFAULT_BAR_THICKNESS,
    OI_PROFILE_DEFAULT_BAR_WIDTH_PERCENT,
    OI_PROFILE_MAX_BAR_THICKNESS,
    OI_PROFILE_MAX_BAR_WIDTH_PERCENT,
    OI_PROFILE_MIN_BAR_THICKNESS,
    OI_PROFILE_MIN_BAR_WIDTH_PERCENT,
    OiProfileColorSettingKey,
    resolveOiProfileColor,
} from '../../drawingTools/oiProfile/oiProfile.constants'
import {
    CP_DEFAULT_BACKGROUND_COLOR,
    CP_DEFAULT_NECK_LINE_COLOR,
    CP_DEFAULT_PANE_LABEL_COLOR,
    CP_DEFAULT_PANE_LABEL_TEXT_COLOR,
    CP_DEFAULT_PRICE_LINE_COLOR,
    CP_DEFAULT_PRICE_LINE_WIDTH,
    CHART_PATTERN_LINE_STYLE_OPTIONS,
    buildPatternDrawingSettingsUpdate,
    getPatternDrawingSettingsMap,
    resolveChartPatternOverlaySettings,
} from '../../drawingTools/chartPatternOverlayTool/chartPatternOverlay.constants'
import {
    buildCandlestickBackgroundStylePatch,
} from '../../../../assets/candlestickPatternConfig'
import {
    isCandlestickPatternIndicator,
    isChartPatternIndicator,
    patternSupportsPriceTarget,
    usesStructuredWedgePatternLayout,
    usesTrianglePatternLayout,
} from '../../../../util/patternIndicators'

const OI_PROFILE_COLOR_SETTINGS = [
    { key: 'primaryCallColor', legacyLabels: ['Primary Call', 'Primary Call Color'] },
    { key: 'secondaryCallColor', legacyLabels: ['Secondary Call', 'Secondary Call Color'] },
    { key: 'primaryPutColor', legacyLabels: ['Primary Put', 'Primary Put Color'] },
    { key: 'secondaryPutColor', legacyLabels: ['Secondary Put', 'Secondary Put Color'] },
] as const

const OI_PROFILE_COLOR_ROWS = [
    { rowLabel: 'Call', keys: ['primaryCallColor', 'secondaryCallColor'] as const },
    { rowLabel: 'Put', keys: ['primaryPutColor', 'secondaryPutColor'] as const },
] as const

const resolveOiProfileColorKey = (colorName: string): string | undefined => {
    const byKey = OI_PROFILE_COLOR_SETTINGS.find((s) => s.key === colorName)
    if (byKey) return byKey.key
    return OI_PROFILE_COLOR_SETTINGS.find(
        (s) => s.legacyLabels.includes(colorName) || s.key === colorName
    )?.key
}

const outputStyleLabelProps = {
    variant: 'body2' as const,
    color: 'text.secondary' as const,
    fontWeight: 400,
}

const settingsRowSx = { py: '8px', px: 0 }

const styleLabelColumnSx = { p: 0, minWidth: '90px' }

const colorNameLabelSx = { p: 0, pl: 2, minWidth: '90px' }

const outputControlsSx = { p: 0, display: 'flex', alignItems: 'center', gap: 1 }

type OiProfileSliderRowProps = {
    label: string
    value: number
    min: number
    max: number
    step?: number
    valueSuffix?: string
    onChange: (value: number) => void
}

const OiProfileSliderRow = ({
    label,
    value,
    min,
    max,
    step = 1,
    valueSuffix = '',
    onChange,
}: OiProfileSliderRowProps) => (
    <Grid container sx={settingsRowSx} alignItems="center">
        <Grid item xs={4} sx={colorNameLabelSx}>
            <Typography {...outputStyleLabelProps}>{label}</Typography>
        </Grid>
        <Grid item xs={8} sx={{ ...outputControlsSx, flexShrink: 0, overflow: 'visible' }}>
            <SettingsNumericSlider
                value={value}
                min={min}
                max={max}
                step={step}
                suffix={valueSuffix}
                onChange={onChange}
            />
        </Grid>
    </Grid>
)

/** Bare numeric titles (e.g. 0, 1) are script indices — not user-facing labels. */
const getColorLabel = (title: string | undefined | null): string | null => {
    if (title == null || title === '') return null
    const label = String(title).trim()
    if (/^\d+$/.test(label)) return null
    return label
}

const hasColors = (colors: SeriesOutput['colors'] | undefined): boolean => (colors?.length ?? 0) > 0

const getConfigurableColors = (colors: SeriesOutput['colors'] | undefined) =>
    (colors ?? []).filter((c) => c?.colorCode && !isAreaFillColorTitle(c.title))

const sectionHeaderRowSx = {
    pt: '8px',
    pb: 0,
    px: 0,
    boxSizing: 'border-box' as const,
    alignItems: 'center',
}

const checkboxRowSx = {
    py: '4px',
    px: 0,
    minHeight: 36,
    boxSizing: 'border-box' as const,
    alignItems: 'center',
}

const plotSettingsCheckboxSx = { p: 0, mr: 0.5 } as const

const plotSettingsCheckboxLabelSx = {
    ml: 0,
    mr: 0,
    alignItems: 'center',
    cursor: 'pointer',
} as const

const outputVisibilityLabelSx = {
    ml: 0,
    mr: 0,
    alignItems: 'center',
    cursor: 'pointer',
    flexWrap: 'nowrap',
} as const

const OutputVisibilityLabel = ({
    title,
    visibility,
    onVisibilityChange,
}: {
    title?: string
    visibility?: boolean
    onVisibilityChange: (visible: boolean) => void
}) => (
    <FormControlLabel
        control={
            <Checkbox
                disableRipple
                size="small"
                sx={{ p: 0, mr: 0.5 }}
                checked={visibility ?? true}
                onChange={(_event, checked) => onVisibilityChange(checked)}
            />
        }
        label={<Typography {...outputStyleLabelProps}>{title}</Typography>}
        sx={outputVisibilityLabelSx}
    />
)

const getOiProfileColorValue = (seriesOutput: SeriesOutput, key: string): string => {
    const colorKey = key as OiProfileColorSettingKey
    const fromSettings = seriesOutput.drawingSettings?.settings?.[key]
    if (fromSettings) {
        return resolveOiProfileColor(colorKey, fromSettings)
    }
    const setting = OI_PROFILE_COLOR_SETTINGS.find((s) => s.key === key)
    const legacyTitles = setting ? [key, ...setting.legacyLabels] : [key]
    const fromColors = seriesOutput.colors?.find((c) => c.title && legacyTitles.includes(c.title))?.colorCode
    if (fromColors) {
        return resolveOiProfileColor(colorKey, fromColors)
    }
    return resolveOiProfileColor(colorKey, undefined)
}

export type SeriesOutputViewProps = {
    onChange: (seriesOutput: SeriesOutput) => void
    seriesOutput: SeriesOutput,
    showLineStyle?: boolean
    lineStyle?: ChartLineStyle
    onLineStyleChange?: (style: ChartLineStyle) => void
    /** Hide the area fill color controls (e.g. for the volume indicator's up/down colors). */
    disableAreaFill?: boolean
    patternIndicator?: Indicator
}
export const SeriesOutputView = ({
    onChange,
    seriesOutput,
    showLineStyle,
    lineStyle,
    onLineStyleChange,
    disableAreaFill,
    patternIndicator,
}: SeriesOutputViewProps) => {
  
    const handleVisibilityChange = (visibility: boolean) => {
        const newOutput = {
            ...seriesOutput,
            visibility: visibility
        };
       onChange(newOutput);

    };
    
    const showPlotTypes = ![IndicatorOutput_OutputType.DRAWING, IndicatorOutput_OutputType.FILL, IndicatorOutput_OutputType.OUTPUT_TYPE_UNKNOWN, IndicatorOutput_OutputType.OUTPUT_TYPE_UNKNOWN].includes(indicatorOutput_OutputTypeFromJSON(seriesOutput.type)) && seriesOutput.title !== 'Open Interest Outlook'

    const getIndicatorOutputTypeFromChartType = (chartType: ChartType, so: SeriesOutput) => {
        switch (chartType) {
            case ChartType.LINE:
                return so.type && [IndicatorOutput_OutputType.HZ_LINE, IndicatorOutput_OutputType.LINE].includes(indicatorOutput_OutputTypeFromJSON(so.type)) ? so.type : IndicatorOutput_OutputType.LINE
            case ChartType.AREA:
                return IndicatorOutput_OutputType.AREA
            case ChartType.HISTOGRAM:
                return IndicatorOutput_OutputType.HISTOGRAM
            case ChartType.COLUMN:
                return IndicatorOutput_OutputType.COLUMN
            case ChartType.FILL:
                return IndicatorOutput_OutputType.FILL
            default:
                return so.type
        }
    }

    const outputType = indicatorOutput_OutputTypeFromJSON(seriesOutput.type)
    const isOiProfileDrawing = outputType === IndicatorOutput_OutputType.DRAWING && seriesOutput.drawingType === 'oiProfile'
    const isClassicChartPatternDrawing = outputType === IndicatorOutput_OutputType.DRAWING
        && seriesOutput.drawingType === 'chartPatternOverlay'
        && isChartPatternIndicator(patternIndicator)
    const isCandlestickPatternDrawing = outputType === IndicatorOutput_OutputType.DRAWING
        && seriesOutput.drawingType === 'chartPatternOverlay'
        && isCandlestickPatternIndicator(patternIndicator)
    const isStructuredChartPatternDrawing = isClassicChartPatternDrawing
    const isTriangleChartPattern = usesTrianglePatternLayout(seriesOutput.drawingSettings)
    const isTradingViewStructuredWedgePattern = usesStructuredWedgePatternLayout(seriesOutput.drawingSettings)
    const patternHasPriceTarget = isClassicChartPatternDrawing
        && patternSupportsPriceTarget(patternIndicator, seriesOutput.drawingSettings)

    const syncDrawingSettingsColor = (colorCode: string, colorName: string) => {
        if (!seriesOutput.drawingType) return seriesOutput.drawingSettings
        const colorKey = seriesOutput.drawingType === 'oiProfile'
            ? resolveOiProfileColorKey(colorName)
            : colorName
        if (!colorKey) return seriesOutput.drawingSettings
        return buildPatternDrawingSettingsUpdate(seriesOutput.drawingSettings, {
            [colorKey]: colorCode,
        })
    }

    const handleColorChange = (colorCode: string, colorName: string) => {
        const newColors = [...(seriesOutput.colors ?? [])]
        const ind = newColors.findIndex((cl) => cl.title === colorName)
        if (ind > -1) {
            newColors[ind] = {
                title: colorName,
                colorCode: colorCode
            }
        }
        onChange({
            ...seriesOutput,
            colors: newColors,
            drawingSettings: syncDrawingSettingsColor(colorCode, colorName),
        })
    }

    const handleDrawingSettingChange = (key: string, value: string | number | boolean) => {
        onChange({
            ...seriesOutput,
            drawingSettings: buildPatternDrawingSettingsUpdate(seriesOutput.drawingSettings, {
                [key]: value,
            }),
        })
    }

    const handleOiProfileDrawingSettingChange = handleDrawingSettingChange

    const handleOiProfileColorChange = (key: string, colorCode: string) => {
        const newColors = [...(seriesOutput.colors ?? [])]
        const setting = OI_PROFILE_COLOR_SETTINGS.find((s) => s.key === key)
        const legacyTitles = setting ? [key, ...setting.legacyLabels] : [key]
        const ind = newColors.findIndex((c) => c.title && legacyTitles.includes(c.title))
        if (ind > -1) {
            newColors[ind] = { title: key, colorCode }
        } else {
            newColors.push({ title: key, colorCode })
        }
        onChange({
            ...seriesOutput,
            colors: newColors,
            drawingSettings: {
                settings: {
                    ...(seriesOutput.drawingSettings?.settings ?? {}),
                    [key]: colorCode,
                },
            },
        })
    }

    const handleThicknessChange = (thickness: LineThickness) => {
        onChange({
            ...seriesOutput,
            thickness,
        })
    }

    const handleAreaFillColorChange = (title: string, colorCode: string) => {
        const newColors = [...(seriesOutput.colors ?? [])]
        const ind = newColors.findIndex((c) => c.title === title)
        if (ind > -1) {
            newColors[ind] = { title, colorCode }
        } else {
            newColors.push({ title, colorCode })
        }
        onChange({
            ...seriesOutput,
            colors: newColors,
        })
    }

    const handlePlotTypeChange = (plotType: PlotTypesMenuItemType) => {
        const newOutput = {
            ...seriesOutput,
            style: plotType.style,
            subType: plotType.subType,
            type: getIndicatorOutputTypeFromChartType(plotType.type, seriesOutput)
        }
        onChange(newOutput)
    }

    const outputChartType = getChartTypeFromOutput(seriesOutput.type || IndicatorOutput_OutputType.LINE)
    const outputStyle = seriesOutput.style || IndicatorOutput_OutputStyle.SOLID
    const outputSubType = seriesOutput.subType || ChartSubType.SIMPLE
    const configurableColors = getConfigurableColors(seriesOutput.colors)
    const isArea = outputChartType === ChartType.AREA
    const showAreaFill = isArea && !disableAreaFill
    const showThickness = outputChartType === ChartType.LINE || isArea

    const areaLineColor = getOutputLineColor(seriesOutput.colors)
    const areaTopFill =
        seriesOutput.colors?.find((c) => c.title === AREA_FILL_TOP_COLOR_TITLE)?.colorCode ??
        (areaLineColor ? tinycolor(areaLineColor).setAlpha(0.4).toHex8String() : '#2962ff66')
    const areaBottomFill =
        seriesOutput.colors?.find((c) => c.title === AREA_FILL_BOTTOM_COLOR_TITLE)?.colorCode ??
        (areaLineColor ? tinycolor(areaLineColor).setAlpha(0).toHex8String() : '#2962ff00')

    const lineThicknessProps = showThickness ? {
        lineThickness: clampLineThickness(seriesOutput.thickness),
        onLineThicknessChange: handleThicknessChange,
        ...(showLineStyle && onLineStyleChange
            ? { lineStyle: lineStyle ?? 'solid', onLineStyleChange }
            : {}),
    } : {}

    if (Object.keys(seriesOutput).length < 1) {
        return null
    }

    if (isStructuredChartPatternDrawing) {
        const patternSettingsMap = getPatternDrawingSettingsMap(seriesOutput.drawingSettings)
        const patternSettings = resolveChartPatternOverlaySettings(patternSettingsMap)
        const getSetting = (key: string, fallback: string | number | boolean) =>
            patternSettingsMap[key] ?? fallback

        const renderPatternLineRow = (
            label: string,
            colorKey: 'priceLineColor' | 'neckLineColor',
            styleKey: 'priceLineStyle' | 'neckLineStyle',
            widthKey: 'priceLineWidth' | 'neckLineWidth',
            defaultColor: string,
            defaultStyle: ChartLineStyle,
            defaultWidth: number
        ) => {
            const colorCode = String(getSetting(colorKey, defaultColor))
            const lineStyle = (getSetting(styleKey, defaultStyle) as ChartLineStyle) ?? defaultStyle
            const lineWidth = Number(getSetting(widthKey, defaultWidth))
            return (
                <Grid container key={label} sx={settingsRowSx} alignItems="center">
                    <Grid item xs={4} sx={colorNameLabelSx}>
                        <Typography {...outputStyleLabelProps}>{label}</Typography>
                    </Grid>
                    <Grid item xs={8} sx={outputControlsSx}>
                        <ColorSelector
                            compact
                            showOnlySwatch
                            color={colorCode}
                            opacity={tinycolor(colorCode).getAlpha()}
                            onColorChange={(clr, transparency) => {
                                handleDrawingSettingChange(colorKey, tinycolor(clr).setAlpha(transparency).toHex8String())
                            }}
                        />
                        <Select
                            size="small"
                            value={lineStyle}
                            sx={{ width: 110, height: 34 }}
                            onChange={({ target }) => handleDrawingSettingChange(styleKey, target.value)}
                        >
                            {CHART_PATTERN_LINE_STYLE_OPTIONS.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                        <SettingsNumericSlider
                            value={lineWidth}
                            min={1}
                            max={4}
                            step={1}
                            onChange={(next) => handleDrawingSettingChange(widthKey, clampLineThickness(next))}
                        />
                    </Grid>
                </Grid>
            )
        }

        return (
            <Grid container alignItems="center">
                <Grid item xs={12} sx={{ p: 0 }}>
                    <Grid container sx={settingsRowSx} alignItems="center">
                        <Grid item xs={12} sx={styleLabelColumnSx}>
                            <OutputVisibilityLabel
                                title={seriesOutput.title}
                                visibility={seriesOutput.visibility}
                                onVisibilityChange={handleVisibilityChange}
                            />
                        </Grid>
                    </Grid>
                    <Box sx={sectionHeaderRowSx} display="flex" alignItems="center">
                        <Typography fontSize="12px" color="#6a6d78">GRAPHIC OBJECTS</Typography>
                    </Box>
                    {(
                        isTradingViewStructuredWedgePattern
                            ? [
                                { key: 'showLabels', label: 'Show Labels', fallback: patternSettings.showPaneLabels },
                                { key: 'showPatternLines', label: 'Show Pattern Lines', fallback: patternSettings.showPatternLines ?? patternSettings.showPolylines },
                                { key: 'showTrendlines', label: 'Show Trendlines', fallback: patternSettings.showTrendlines ?? patternSettings.showLines },
                                { key: 'showLineFills', label: 'Show Background', fallback: patternSettings.showBackground },
                                ...(patternHasPriceTarget
                                    ? [{ key: 'showPriceTarget', label: 'Price Target', fallback: patternSettings.showPriceTarget ?? true }]
                                    : []),
                            ]
                            : [
                                { key: 'showLabels', label: 'Labels', fallback: patternSettings.showPaneLabels },
                                { key: 'showLineFills', label: 'LineFills', fallback: patternSettings.showBackground },
                                { key: 'showLines', label: 'Lines', fallback: patternSettings.showLines },
                                { key: 'showPolylines', label: 'Polylines', fallback: patternSettings.showPolylines },
                                ...(patternHasPriceTarget
                                    ? [{ key: 'showPriceTarget', label: 'Price Target', fallback: patternSettings.showPriceTarget ?? true }]
                                    : []),
                            ]
                    ).map(({ key, label, fallback }) => (
                        <Box key={key} sx={{ ...checkboxRowSx, display: 'flex', alignItems: 'center' }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        disableRipple
                                        size="small"
                                        sx={plotSettingsCheckboxSx}
                                        checked={Boolean(getSetting(key, fallback))}
                                        onChange={(_event, checked) => {
                                            handleDrawingSettingChange(key, checked)
                                            if (key === 'showLabels') handleDrawingSettingChange('showPaneLabels', checked)
                                            if (key === 'showLineFills') handleDrawingSettingChange('showBackground', checked)
                                            if (key === 'showPatternLines') {
                                                handleDrawingSettingChange('showPolylines', checked)
                                            }
                                            if (key === 'showTrendlines') {
                                                handleDrawingSettingChange('showLines', checked)
                                            }
                                        }}
                                    />
                                }
                                label={<Typography {...outputStyleLabelProps}>{label}</Typography>}
                                sx={plotSettingsCheckboxLabelSx}
                            />
                        </Box>
                    ))}
                    <Box sx={{ ...sectionHeaderRowSx, pt: 1 }} display="flex" alignItems="center">
                        <Typography fontSize="12px" color="#6a6d78">STYLE</Typography>
                    </Box>
                    {renderPatternLineRow(
                        'Price Line',
                        'priceLineColor',
                        'priceLineStyle',
                        'priceLineWidth',
                        CP_DEFAULT_PRICE_LINE_COLOR,
                        'solid',
                        CP_DEFAULT_PRICE_LINE_WIDTH
                    )}
                    {renderPatternLineRow(
                        isTriangleChartPattern ? 'Triangle' : 'Neck line',
                        'neckLineColor',
                        'neckLineStyle',
                        'neckLineWidth',
                        CP_DEFAULT_NECK_LINE_COLOR,
                        'dotted',
                        CP_DEFAULT_PRICE_LINE_WIDTH
                    )}
                    <Grid container sx={settingsRowSx} alignItems="center">
                        <Grid item xs={4} sx={colorNameLabelSx}>
                            <Typography {...outputStyleLabelProps}>Background</Typography>
                        </Grid>
                        <Grid item xs={8} sx={outputControlsSx}>
                            <Checkbox
                                disableRipple
                                size="small"
                                checked={Boolean(getSetting('showBackground', patternSettings.showBackground))}
                                onChange={(_event, checked) => handleDrawingSettingChange('showBackground', checked)}
                            />
                            <ColorSelector
                                compact
                                showOnlySwatch
                                color={String(getSetting('backgroundColor', CP_DEFAULT_BACKGROUND_COLOR))}
                                opacity={tinycolor(String(getSetting('backgroundColor', CP_DEFAULT_BACKGROUND_COLOR))).getAlpha()}
                                onColorChange={(clr, transparency) => {
                                    handleDrawingSettingChange('backgroundColor', tinycolor(clr).setAlpha(transparency).toHex8String())
                                }}
                            />
                        </Grid>
                    </Grid>
                    <Grid container sx={settingsRowSx} alignItems="center">
                        <Grid item xs={4} sx={colorNameLabelSx}>
                            <Typography {...outputStyleLabelProps}>Pane labels</Typography>
                        </Grid>
                        <Grid item xs={8} sx={outputControlsSx}>
                            <Checkbox
                                disableRipple
                                size="small"
                                checked={Boolean(getSetting('showPaneLabels', patternSettings.showPaneLabels))}
                                onChange={(_event, checked) => {
                                    handleDrawingSettingChange('showPaneLabels', checked)
                                    handleDrawingSettingChange('showLabels', checked)
                                }}
                            />
                            <ColorSelector
                                compact
                                showOnlySwatch
                                color={String(getSetting('paneLabelColor', CP_DEFAULT_PANE_LABEL_COLOR))}
                                opacity={1}
                                onColorChange={(clr, transparency) => {
                                    handleDrawingSettingChange('paneLabelColor', tinycolor(clr).setAlpha(transparency).toHex8String())
                                }}
                            />
                            <ColorSelector
                                compact
                                showOnlySwatch
                                color={String(getSetting('paneLabelTextColor', CP_DEFAULT_PANE_LABEL_TEXT_COLOR))}
                                opacity={1}
                                onColorChange={(clr, transparency) => {
                                    handleDrawingSettingChange('paneLabelTextColor', tinycolor(clr).setAlpha(transparency).toHex8String())
                                }}
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        )
    }

    if (isCandlestickPatternDrawing) {
        const patternSettingsMap = getPatternDrawingSettingsMap(seriesOutput.drawingSettings)
        const patternSettings = resolveChartPatternOverlaySettings(patternSettingsMap)
        const defaultBackgroundColor = patternSettings.backgroundColor
        const getSetting = (key: string, fallback: string | number | boolean) =>
            patternSettingsMap[key] ?? fallback

        const updateDrawingSettings = (patch: Record<string, unknown>) => {
            onChange({
                ...seriesOutput,
                drawingSettings: buildPatternDrawingSettingsUpdate(seriesOutput.drawingSettings, patch),
            })
        }

        return (
            <Grid container alignItems="center">
                <Grid item xs={12} sx={{ p: 0 }}>
                    <Box sx={{ ...checkboxRowSx, display: 'flex', alignItems: 'center' }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    disableRipple
                                    size="small"
                                    sx={plotSettingsCheckboxSx}
                                    checked={Boolean(getSetting('showBackground', patternSettings.showBackground))}
                                    onChange={(_event, checked) => {
                                        updateDrawingSettings(
                                            buildCandlestickBackgroundStylePatch(
                                                String(getSetting('backgroundColor', defaultBackgroundColor)),
                                                checked,
                                            ),
                                        )
                                    }}
                                />
                            }
                            label={<Typography {...outputStyleLabelProps}>Background Color</Typography>}
                            sx={plotSettingsCheckboxLabelSx}
                        />
                        <ColorSelector
                            compact
                            showOnlySwatch
                            color={String(getSetting('backgroundColor', defaultBackgroundColor))}
                            opacity={tinycolor(String(getSetting('backgroundColor', defaultBackgroundColor))).getAlpha()}
                            onColorChange={(clr, transparency) => {
                                const backgroundHex8 = tinycolor(clr).setAlpha(transparency).toHex8String()
                                updateDrawingSettings(
                                    buildCandlestickBackgroundStylePatch(
                                        backgroundHex8,
                                        Boolean(getSetting('showBackground', patternSettings.showBackground)),
                                    ),
                                )
                            }}
                        />
                    </Box>
                    <Box sx={sectionHeaderRowSx} display="flex" alignItems="center">
                        <Typography fontSize="12px" color="#6a6d78">GRAPHIC OBJECTS</Typography>
                    </Box>
                    <Box sx={{ ...checkboxRowSx, display: 'flex', alignItems: 'center' }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    disableRipple
                                    size="small"
                                    sx={plotSettingsCheckboxSx}
                                    checked={Boolean(getSetting('showPaneLabels', patternSettings.showPaneLabels))}
                                    onChange={(_event, checked) => {
                                        updateDrawingSettings({
                                            showPaneLabels: checked,
                                            showLabels: checked,
                                        })
                                    }}
                                />
                            }
                            label={<Typography {...outputStyleLabelProps}>Pane labels</Typography>}
                            sx={plotSettingsCheckboxLabelSx}
                        />
                    </Box>
                </Grid>
            </Grid>
        )
    }

    if (isOiProfileDrawing) {
        return (
            <Grid container alignItems="center">
                <Grid item xs={12} sx={{ p: 0 }}>
                    <Grid container sx={settingsRowSx} alignItems="center">
                        <Grid item xs={12} sx={styleLabelColumnSx}>
                            <OutputVisibilityLabel
                                title={seriesOutput.title}
                                visibility={seriesOutput.visibility}
                                onVisibilityChange={handleVisibilityChange}
                            />
                        </Grid>
                    </Grid>
                    {OI_PROFILE_COLOR_ROWS.map(({ rowLabel, keys }) => (
                        <Grid container key={rowLabel} sx={settingsRowSx} alignItems="center">
                            <Grid item xs={4} sx={colorNameLabelSx}>
                                <Typography {...outputStyleLabelProps}>{rowLabel}</Typography>
                            </Grid>
                            <Grid item xs={8} sx={outputControlsSx}>
                                {keys.map((key) => {
                                    const colorCode = getOiProfileColorValue(seriesOutput, key)
                                    const tColor = tinycolor(colorCode)
                                    return (
                                        <ColorSelector
                                            key={key}
                                            compact
                                            showOnlySwatch
                                            color={colorCode}
                                            opacity={tColor.getAlpha()}
                                            onColorChange={(clr, transparency) => {
                                                const newColor = tinycolor(clr).setAlpha(transparency).toHex8String()
                                                handleOiProfileColorChange(key, newColor)
                                            }}
                                        />
                                    )
                                })}
                            </Grid>
                        </Grid>
                    ))}
                    <OiProfileSliderRow
                        label="Bar thickness"
                        value={clampOiProfileBarThickness(
                            Number(seriesOutput.drawingSettings?.settings?.barThickness ?? OI_PROFILE_DEFAULT_BAR_THICKNESS)
                        )}
                        min={OI_PROFILE_MIN_BAR_THICKNESS}
                        max={OI_PROFILE_MAX_BAR_THICKNESS}
                        onChange={(next) =>
                            handleOiProfileDrawingSettingChange('barThickness', clampOiProfileBarThickness(next))
                        }
                    />
                    <OiProfileSliderRow
                        label="Bar width (%)"
                        value={clampOiProfileBarWidthPercent(
                            Number(seriesOutput.drawingSettings?.settings?.barWidthPercent ?? OI_PROFILE_DEFAULT_BAR_WIDTH_PERCENT)
                        )}
                        min={OI_PROFILE_MIN_BAR_WIDTH_PERCENT}
                        max={OI_PROFILE_MAX_BAR_WIDTH_PERCENT}
                        valueSuffix="%"
                        onChange={(next) =>
                            handleOiProfileDrawingSettingChange('barWidthPercent', clampOiProfileBarWidthPercent(next))
                        }
                    />
                </Grid>
            </Grid>
        )
    }

    return (
        <Grid container alignItems="center">
            <Grid item xs={12} sx={{ p: 0 }}>

                {
                    configurableColors.length > 1 ? (
                        <>
                            <Grid container sx={settingsRowSx} alignItems="center">
                                <Grid item xs={4} sx={styleLabelColumnSx}>
                                    <OutputVisibilityLabel
                                        title={seriesOutput.title}
                                        visibility={seriesOutput.visibility}
                                        onVisibilityChange={handleVisibilityChange}
                                    />
                                </Grid>
                            </Grid>
                            {configurableColors.map((color, colorIndex) => {
                                const tColor = tinycolor(color.colorCode)
                                const colorLabel = getColorLabel(color.title)
                                return (
                                    <Grid container key={color?.title ?? colorIndex} sx={settingsRowSx} alignItems="center">
                                        <Grid item xs={4} sx={colorNameLabelSx}>
                                            {colorLabel ? (
                                                <Typography {...outputStyleLabelProps}>{colorLabel}</Typography>
                                            ) : null}
                                        </Grid>
                                        <Grid item xs={8} sx={outputControlsSx}>
                                            <ColorSelector
                                                compact
                                                showOnlySwatch
                                                color={color.colorCode}
                                                opacity={tColor.getAlpha()}
                                                onColorChange={(clr, transparency) => {
                                                    const newColor = tinycolor(clr).setAlpha(transparency).toHex8String()
                                                    handleColorChange(newColor, color.title)
                                                }}
                                                {...(colorIndex === 0 ? lineThicknessProps : {})}
                                            />
                                            {colorIndex === 0 && showPlotTypes ? (
                                                <SeriesOutputPlotTypesView
                                                    outputChartType={outputChartType}
                                                    outputStyle={outputStyle}
                                                    outputSubType={outputSubType}
                                                    onSelected={handlePlotTypeChange}
                                                />
                                            ) : null}
                                        </Grid>
                                    </Grid>
                                )
                            })}
                        </>) : (
                        <Grid container sx={settingsRowSx} alignItems="center">
                            <Grid item xs={6} sx={styleLabelColumnSx}>
                                <OutputVisibilityLabel
                                    title={seriesOutput.title}
                                    visibility={seriesOutput.visibility}
                                    onVisibilityChange={handleVisibilityChange}
                                />
                            </Grid>
                            <Grid item xs={6} sx={outputControlsSx}>
                                <>
                                    {hasColors(seriesOutput.colors) ? <ColorSelector compact showOnlySwatch color={seriesOutput.colors[0].colorCode} opacity={tinycolor(seriesOutput.colors[0].colorCode).getAlpha()}
                                                onColorChange={(clr, transparency) => {
                                                    const newColor = tinycolor(clr).setAlpha(transparency).toHex8String();
                                                    handleColorChange(newColor, seriesOutput.colors?.[0].title ?? '');
                                                }}
                                                {...lineThicknessProps}
                                            /> : null}
                                    {showPlotTypes ? <SeriesOutputPlotTypesView
                                        outputChartType={outputChartType}
                                        outputStyle={outputStyle}
                                        outputSubType={outputSubType}
                                        onSelected={handlePlotTypeChange} /> : null}
                                </>
                            </Grid>
                        </Grid>
                    )
                }
                {showAreaFill ? (
                    <Grid container sx={settingsRowSx} alignItems="center">
                        <Grid item xs={6} sx={styleLabelColumnSx}>
                            <Typography {...outputStyleLabelProps}>Fill</Typography>
                        </Grid>
                        <Grid item xs={6} sx={outputControlsSx}>
                            <ColorSelector
                                compact
                                showOnlySwatch
                                color={tinycolor(areaTopFill).toHexString()}
                                opacity={tinycolor(areaTopFill).getAlpha()}
                                onColorChange={(clr, transparency) => {
                                    const newColor = tinycolor(clr).setAlpha(transparency).toHex8String()
                                    handleAreaFillColorChange(AREA_FILL_TOP_COLOR_TITLE, newColor)
                                }}
                            />
                            <ColorSelector
                                compact
                                showOnlySwatch
                                color={tinycolor(areaBottomFill).toHexString()}
                                opacity={tinycolor(areaBottomFill).getAlpha()}
                                onColorChange={(clr, transparency) => {
                                    const newColor = tinycolor(clr).setAlpha(transparency).toHex8String()
                                    handleAreaFillColorChange(AREA_FILL_BOTTOM_COLOR_TITLE, newColor)
                                }}
                            />
                        </Grid>
                    </Grid>
                ) : null}
            </Grid>
        </Grid>
    )
}

export type SeriesGroupOutputProps = {
    seriesOutputsConfig: Record<string, SeriesOutput>,
    seriesOrder?: string[],
    /** Plot titles the indicator script actually drew on the last run. */
    activePlotTitles?: ReadonlySet<string>,
    onSeriesOutputChange: (key: string, seriesOutput: SeriesOutput) => void
    showLineStyle?: boolean
    lineStyle?: ChartLineStyle
    onLineStyleChange?: (style: ChartLineStyle) => void
    disableAreaFill?: boolean
    patternIndicator?: Indicator
}

const SeriesGroupOutputs = ({
    seriesOutputsConfig,
    seriesOrder,
    activePlotTitles,
    onSeriesOutputChange,
    showLineStyle,
    lineStyle,
    onLineStyleChange,
    disableAreaFill,
    patternIndicator,
}: SeriesGroupOutputProps) => {

    const keys = useMemo(() => {
        const configKeys = new Set(Object.keys(seriesOutputsConfig))
        let ordered: string[]
        if (seriesOrder?.length) {
            ordered = seriesOrder.filter((id) => configKeys.has(id))
            const remaining = Object.keys(seriesOutputsConfig).filter((id) => !ordered.includes(id))
            ordered = [...ordered, ...remaining]
        } else {
            ordered = Object.keys(seriesOutputsConfig)
        }
        if (activePlotTitles) {
            return ordered.filter((id) => {
                const title = seriesOutputsConfig[id]?.title
                return title != null && activePlotTitles.has(title)
            })
        }
        return ordered
    }, [seriesOutputsConfig, seriesOrder, activePlotTitles, activePlotTitles ? Array.from(activePlotTitles).join('\0') : ''])

    return (
        <>
            {keys.map((key) => (
                <SeriesOutputView
                    key={key}
                    onChange={(seriesOutput: SeriesOutput) => onSeriesOutputChange(key, seriesOutput)}
                    seriesOutput={seriesOutputsConfig[key]}
                    showLineStyle={showLineStyle}
                    lineStyle={lineStyle}
                    onLineStyleChange={onLineStyleChange}
                    disableAreaFill={disableAreaFill}
                    patternIndicator={patternIndicator}
                />
            ))}
        </>
    )
}

export default memo(SeriesGroupOutputs)

